import React, { useEffect, useRef, useState } from 'react';
import { authHeaders } from '../../api.js';
import { openLiveSyncConversation } from '../../lib/live-codex.js';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';
type Task = { sync_run_id?: unknown; status?: unknown; created_at?: unknown; priority?: unknown };
const UUID = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

/** Entry only: opens the same durable Sync conversation as the Sync page.
 * No second runner, automatic inference, legacy fallback or budget consent. */
export function LiveCodexSessionPanel({ projectId, onNavigate }: {
  projectId: string; onNavigate?: (page: string) => void;
}): React.ReactElement | null {
  const [selection, setSelection] = useState<{ projectId: string; syncId: string; p0: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const generation = useRef(0);
  useEffect(() => {
    const token = ++generation.current;
    const controller = new AbortController();
    setSelection(null); setBusy(false); setNotice('');
    void (async () => {
      try {
        const headers = await authHeaders();
        if (!headers.Authorization || controller.signal.aborted) return;
        const response = await fetch(`${API_BASE}/api/sync/tasks/${encodeURIComponent(projectId)}?limit=500`, {
          headers, cache: 'no-store', signal: controller.signal,
        });
        if (!response.ok) return;
        const body: unknown = await response.json();
        if (!body || typeof body !== 'object' || !('tasks' in body) || !Array.isArray(body.tasks)) return;
        const tasks = body.tasks.filter((t: unknown): t is Task => !!t && typeof t === 'object');
        const open = tasks.filter(t => typeof t.sync_run_id === 'string' && UUID.test(t.sync_run_id)
          && (t.status === 'open' || t.status === 'acknowledged'));
        const latest = open.reduce<Task | null>((best, t) => {
          const score = (v: Task) => typeof v.created_at === 'string' ? Date.parse(v.created_at) || 0 : 0;
          return !best || score(t) > score(best) ? t : best;
        }, null);
        if (generation.current !== token || controller.signal.aborted || !latest) return;
        setSelection({ projectId, syncId: latest.sync_run_id as string,
          p0: open.filter(t => t.sync_run_id === latest.sync_run_id && t.priority === 'P0').length });
      } catch { /* Findings remain available; no unverified entry is offered. */ }
    })();
    return () => { ++generation.current; controller.abort(); };
  }, [projectId]);

  if (!selection || selection.projectId !== projectId || !onNavigate) return null;
  const open = async () => {
    if (busy) return;
    const token = generation.current;
    setBusy(true); setNotice('');
    try {
      const destination = await openLiveSyncConversation(selection.syncId);
      if (generation.current === token) onNavigate(destination);
    } catch {
      if (generation.current === token) setNotice('Codex could not open yet. Your findings are preserved. Try again.');
    } finally { if (generation.current === token) setBusy(false); }
  };
  return <section data-testid="live-codex-panel" aria-label="Fix with Codex" className="card">
    <p>{selection.p0 > 0 ? `${selection.p0} critical (P0) finding${selection.p0 === 1 ? '' : 's'}. ` : ''}
      Open this Sync's Codex conversation to fix findings P0 first, on a branch and pull request for your approval.</p>
    <p>The approved cap includes resumed turns. No automatic top-up.</p>
    <button type="button" disabled={busy} onClick={() => { void open(); }}>
      {busy ? 'Opening Codex...' : selection.p0 > 0 ? 'Fix with Codex (P0 first)' : 'Fix with Codex'}
    </button>
    {notice && <p role="status">{notice}</p>}
  </section>;
}
